import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { getOrder, listOrders } from "../../services/studioOrderApi";
import { unwrapGeoPayload } from "../gi-registry/geoPath";
import { fallbackOrderOption, orderToOption, StudioOrderOption } from "./orderPicker";
import "./orders.scss";

type Props = {
  valueId?: string;
  valueCode?: string;
  onChange: (next: { id: string; code: string }) => void;
  disabled?: boolean;
  /** Stacking of the open list; raise it inside a dialog or sheet so the list shows above it. */
  menuZIndex?: number;
  /** The line under the field; defaults to the product wording. */
  hint?: string;
};

const SEARCH_LIMIT = 25;

const mergeOptions = (
  rows: StudioOrderOption[],
  selected: StudioOrderOption | null
): StudioOrderOption[] => {
  if (!selected) {
    return rows;
  }
  if (rows.some((row) => row.value === selected.value || row.code === selected.code)) {
    return rows.map((row) =>
      row.value === selected.value || row.code === selected.code ? selected : row
    );
  }
  return [selected, ...rows];
};

const StudioOrderSelect = ({ valueId = "", valueCode = "", onChange, disabled = false, menuZIndex = 40, hint }: Props) => {
  const [options, setOptions] = useState<StudioOrderOption[]>([]);
  const [selected, setSelected] = useState<StudioOrderOption | null>(
    fallbackOrderOption(valueId, valueCode)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);
  const selectedRef = useRef<StudioOrderOption | null>(selected);
  const searchTimer = useRef<number | null>(null);
  useEffect(() => {
    selectedRef.current = selected;
  });

  const loadOptions = async (q: string, keep: StudioOrderOption | null) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res: any = await listOrders({
        offset: 0,
        limit: SEARCH_LIMIT,
        q: q.trim() || undefined,
      });
      if (seq !== requestSeq.current) {
        return;
      }
      if (!res?.success) {
        setError(res?.message || "Could not load orders");
        setOptions(keep ? [keep] : []);
        return;
      }
      const docs = Array.isArray(res?.data?.docs) ? res.data.docs : [];
      const mapped = docs
        .map(orderToOption)
        .filter((item: StudioOrderOption | null): item is StudioOrderOption => Boolean(item));
      setOptions(mergeOptions(mapped, keep));
      setError("");
    } catch {
      if (seq !== requestSeq.current) {
        return;
      }
      setError("Could not load orders");
      setOptions(keep ? [keep] : []);
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOptions("", fallbackOrderOption(valueId, valueCode));
    // Initial studio list only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const id = String(valueId || "").trim();
    const code = String(valueCode || "").trim();
    if (!id && !code) {
      setSelected(null);
      return;
    }
    if (selected && selected.value === id && (!code || selected.code === code) && selected.customerName) {
      return;
    }
    const apply = (option: StudioOrderOption | null) => {
      if (cancelled || !option) {
        return;
      }
      setSelected(option);
      setOptions((current) => mergeOptions(current, option));
    };
    const loadSelected = async () => {
      if (id) {
        try {
          const res: any = await getOrder(id);
          if (cancelled) {
            return;
          }
          if (res?.success) {
            apply(orderToOption(unwrapGeoPayload(res) || {}));
            return;
          }
        } catch {
          // Fall through to the code-only option.
        }
      }
      if (code) {
        try {
          const res: any = await listOrders({ offset: 0, limit: 5, q: code });
          if (cancelled) {
            return;
          }
          const docs = Array.isArray(res?.data?.docs) ? res.data.docs : [];
          const match = docs
            .map(orderToOption)
            .find((item: StudioOrderOption | null) => item && (item.value === id || item.code === code));
          if (match) {
            apply(match);
            return;
          }
        } catch {
          // Fall through to the code-only option.
        }
      }
      apply(fallbackOrderOption(id, code));
    };
    loadSelected();
    return () => {
      cancelled = true;
      if (searchTimer.current) {
        window.clearTimeout(searchTimer.current);
      }
    };
    // Re-load when create-product lands with ?orderId= / ?orderCode=.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueId, valueCode]);

  return (
    <>
      <Select
        className="React_selector studio-order-select"
        classNamePrefix="react-select"
        inputId="studioOrderCode"
        name="studioOrderCode"
        placeholder="Search order number, customer, or design"
        isClearable
        isSearchable
        isDisabled={disabled}
        isLoading={loading}
        filterOption={() => true}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: menuZIndex }),
          menu: (base) => ({
            ...base,
            minWidth: "100%",
            width: "max(100%, 280px)",
            textTransform: "none",
          }),
          option: (base) => ({ ...base, textTransform: "none" }),
          singleValue: (base) => ({ ...base, textTransform: "none" }),
          input: (base) => ({ ...base, textTransform: "none" }),
          placeholder: (base) => ({ ...base, textTransform: "none" }),
        }}
        options={options}
        value={selected}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No matching orders" : "No orders recorded yet"
        }
        onInputChange={(value, meta) => {
          if (meta.action !== "input-change") {
            return;
          }
          if (searchTimer.current) {
            window.clearTimeout(searchTimer.current);
          }
          searchTimer.current = window.setTimeout(() => {
            loadOptions(value, selectedRef.current);
          }, 300);
        }}
        onChange={(option) => {
          const next = (option as StudioOrderOption | null) || null;
          setSelected(next);
          onChange({
            id: next?.value || "",
            code: next?.code || "",
          });
        }}
        formatOptionLabel={(option, { context }) => {
          if (context === "value") {
            return (
              <span className="studio-order-option__value">
                {option.code}
                {option.customerName ? ` · ${option.customerName}` : ""}
              </span>
            );
          }
          return (
            <div className="studio-order-option">
              <div className="studio-order-option__title">{option.code}</div>
              <div className="studio-order-option__detail">
                {[option.customerName || "No customer name", option.productDesign]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              <div className="studio-order-option__meta">
                {[
                  option.colours,
                  option.status,
                  option.dueDate ? `Due ${option.dueDate}` : "",
                  option.orgName,
                ]
                  .filter(Boolean)
                  .join(" · ") || "No colour or status yet"}
              </div>
            </div>
          );
        }}
      />
      {error ? (
        <p className="RequiredField" style={{ paddingTop: 6 }}>
          {error}
        </p>
      ) : (
        <p className="studio-order-select__hint">
          {hint || "Pick the studio order this product belongs to. Search by order number, customer, or design."}
        </p>
      )}
    </>
  );
};

export default StudioOrderSelect;
